// Component dependencies.
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import MasonryComponent from 'react-masonry-component';
import InfiniteScroll from '../../../../common/components/InfiniteScroll.jsx';
import _ from 'lodash';

import enums from '../../config/enums';

import { getMasonryColWidthSelector } from '../../utils/collection-utils.js';

/**
 * Simple fade-in animation component to replace VelocityTransitionGroup(velocity-react)
 * Animates each child element individually when they are added to the DOM.
 * Cleans up references when children are removed, allowing re-animation if re-added.
 */
const TransitionGroup = ({ children }) => {
  const containerRef = useRef(null);
  const animatedElementsRef = useRef(new Set());

  useEffect(() => {
    if (!containerRef.current) return;

    const childElements = containerRef.current.querySelectorAll(':scope > *');
    const currentChildren = new Set(childElements);

    // Clean up removed children from tracking
    animatedElementsRef.current.forEach((element) => {
      if (!currentChildren.has(element)) {
        animatedElementsRef.current.delete(element);
      }
    });

    // Animate new children
    childElements.forEach((element) => {
      if (!animatedElementsRef.current.has(element)) {
        animatedElementsRef.current.add(element);

        element.style.visibility = 'hidden';
        element.style.opacity = '0';

        //double 'requestAnimationFrame' is needed, because with just one of them, we could end up in a situation where the browser just batches the update and skips the transition, so it’s important that the opacity: 0 is drawn to the screen.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            element.style.transition = 'opacity 500ms, visibility 500ms';
            element.style.visibility = 'visible';
            element.style.opacity = '1';

            const handleTransitionEnd = () => {
              element.style.transition = '';
              element.removeEventListener('transitionend', handleTransitionEnd);
            };

            element.addEventListener('transitionend', handleTransitionEnd);
          });
        });
      }
    });
  }, [children]);

  return <div ref={containerRef}>{children}</div>;
};

/**
 * <pre>
 * Display Item > SocialContentWall React components.
 *
 * Summary of responsibilities:
 *
 *  - set of components and helper functions to handle rendering and functionality for SocialContentWall of Display Item elements.
 * </pre>
 *
 * @author Various
 * @namespace
 */

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Display Item > Collection > List React component.
 *
 * Summary of responsibilities:
 *
 *  - outputs wrapper elements for List collections.
 * </pre>
 *
 * @memberOf
 * @namespace
 *
 * @return {Common.Components.DisplayItems.Collections.CollectionList}
 */
class List extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.Elements.Collections.CollectionList}
   */
  render() {
    const classNames = this.props.className || 'xContainer';
    return (
      <div className={classNames} data-ngx-contentsource="external">
        <div className="xContainerInner">{this.props.children}</div>
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} className List of css classnames to apply to the containing element.
 */
List.propTypes = {
  className: PropTypes.string,
};

export { List };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Display Item > Collection > Grid React component.
 *
 * Summary of responsibilities:
 *
 *  - outputs wrapper elements Grid collections.
 * </pre>
 *
 * @memberOf Common.Components.DisplayItems.Collections
 * @namespace
 *
 * @return {Common.Components.DisplayItems.Collections.CollectionGrid}
 */
class Grid extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.Elements.Collections.CollectionGrid}
   */
  render() {
    const classNames = this.props.className || 'xContainer';
    return (
      <div className={classNames} data-ngx-contentsource="external">
        <div className="xContainerInner" ref={this.setContainerRef}>
          <Choose>
            <When condition={this.props.showMoreMode === enums.SHOW_MORE_MODE_AUTO}>
              <InfiniteScroll
                onLoadMore={this.props.onLoadMore}
                hasMore={this.props.currentPage < this.props.totalPages}
              >
                <TransitionGroup>{this.props.children}</TransitionGroup>
              </InfiniteScroll>
            </When>
            <Otherwise>
              <TransitionGroup>{this.props.children}</TransitionGroup>
            </Otherwise>
          </Choose>
        </div>
      </div>
    );
  }
}

Grid.propTypes = {
  className: PropTypes.string,
};

export { Grid };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Display Item > Collection > Masonry React component.
 *
 * Summary of responsibilities:
 *
 *  - outputs wrapper elements for Masonry collections.
 * </pre>
 *
 * @memberOf Common.Components.DisplayItems.Collections
 * @namespace
 *
 * @return {Common.Components.DisplayItems.Collections.CollectionMasonry}
 */
class Masonry extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
    this.handleRef = this.handleRef.bind(this);
  }

  handleRef(refName) {
    this[`set${refName}`] = (domEle) => (this[refName] = domEle);
    return this[`set${refName}`];
  }
  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.Elements.Collections.CollectionMasonry}
   */
  render() {
    const classNames = this.props.className || 'xContainer';
    const masonryColWidthSelector = getMasonryColWidthSelector(this.props.children);
    return (
      <div className={classNames} data-ngx-contentsource="external">
        <div className="xContainerInner" ref={this.handleRef('container')}>
          <Choose>
            <When condition={this.props.showMoreMode === enums.SHOW_MORE_MODE_AUTO}>
              <InfiniteScroll
                onLoadMore={this.props.onLoadMore}
                hasMore={this.props.currentPage < this.props.totalPages}
              >
                <MasonryComponent
                  options={{
                    transitionDuration: 500,
                    stagger:            50,
                    columnWidth:        masonryColWidthSelector,
                  }}
                  onLayoutComplete={NGX.App.api.resizeParent}
                >
                  {this.props.children}
                </MasonryComponent>
              </InfiniteScroll>
            </When>
            <Otherwise>
              <MasonryComponent
                options={{
                  transitionDuration: 500,
                  stagger:            50,
                  columnWidth:        masonryColWidthSelector,
                }}
                onLayoutComplete={NGX.App.api.resizeParent}
              >
                {this.props.children}
              </MasonryComponent>
            </Otherwise>
          </Choose>
        </div>
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} className List of css classnames to apply to the containing element.
 */
Masonry.propTypes = {
  className: PropTypes.string,
};

export { Masonry };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Display Item > Collection > Column React component.
 *
 * Summary of responsibilities:
 *
 *  - outputs wrapper elements for Column collections.
 * </pre>
 *
 * @memberOf Common.Components.DisplayItems.Collections
 * @namespace
 *
 * @return {Common.Components.DisplayItems.Collections.CollectionColumn}
 */
class Column extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
    this.handleRef = this.handleRef.bind(this);
  }

  handleRef(refName) {
    this[`set${refName}`] = (domEle) => (this[refName] = domEle);
    return this[`set${refName}`];
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.Elements.Collections.CollectionColumn}
   */
  render() {
    const classNames = this.props.className || 'xContainer';
    const masonryColWidthSelector = getMasonryColWidthSelector(this.props.children);

    return (
      <div className={classNames} data-ngx-contentsource="external">
        <div className="xContainerInner" ref={this.handleRef('container')}>
          <Choose>
            <When condition={this.props.showMoreMode === enums.SHOW_MORE_MODE_AUTO}>
              <InfiniteScroll
                onLoadMore={this.props.onLoadMore}
                hasMore={this.props.currentPage < this.props.totalPages}
              >
                <MasonryComponent
                  options={{
                    transitionDuration: 500,
                    stagger:            50,
                    columnWidth:        masonryColWidthSelector,
                  }}
                  onLayoutComplete={NGX.App.api.resizeParent}
                >
                  {this.props.children}
                </MasonryComponent>
              </InfiniteScroll>
            </When>
            <Otherwise>
              <MasonryComponent
                options={{
                  transitionDuration: 500,
                  stagger:            50,
                  columnWidth:        masonryColWidthSelector,
                }}
                onLayoutComplete={NGX.App.api.resizeParent}
              >
                {this.props.children}
              </MasonryComponent>
            </Otherwise>
          </Choose>
        </div>
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} className List of css classnames to apply to the containing element.
 */
Column.propTypes = {
  className: PropTypes.string,
};

export { Column };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Display Item > Collection > Waterfall React component.
 *
 * Summary of responsibilities:
 *
 *  - outputs wrapper elements for Waterfall collections.
 * </pre>
 *
 * @memberOf Common.Components.DisplayItems.Collections
 * @namespace
 *
 * @return {Common.Components.Modules.Collections.Waterfall}
 */
class Waterfall extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
    this.state = { waterfallCount: 0, itemsCols: [], lastColUpdated: -1 };

    this.onResize = this.onResize.bind(this);
    this.calculateColumns = this.calculateColumns.bind(this);
    this.tryPrependItems = this.tryPrependItems.bind(this);
    this.garbageCollectColumns = this.garbageCollectColumns.bind(this);
    this.generateColumns = this.generateColumns.bind(this);
    this.handleRef = this.handleRef.bind(this);
    this.injectItem = this.injectItem.bind(this);
    this.getPinnedValue = this.getPinnedValue.bind(this);
    this.isItemPinned = this.isItemPinned.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
    this.onResize();
  }

  componentDidUpdate(prevProps) {
    if (this.props.children !== prevProps.children) {
      const currentChildren = React.Children.toArray(this.props.children);
      const prevChildren = React.Children.toArray(prevProps.children);

      const shouldPrependAndGarbageCollect =
        this.shouldPrepend(this.props.newItems) && currentChildren.length > enums.CONTENT_CAP;

      if (shouldPrependAndGarbageCollect) {
        let { updatedCols, colToUpdate } = this.tryPrependItems(this.props.newItems, this.props.moduleOpts, this.props);
        const itemsToRemove = _.takeRight(prevChildren, this.props.newItems.length);
        updatedCols = this.garbageCollectColumns(itemsToRemove, updatedCols);
        this.setState({ itemsCols: updatedCols, lastColUpdated: colToUpdate });
      } else if (this.shouldPrepend(this.props.newItems)) {
        const { updatedCols, colToUpdate } = this.tryPrependItems(
          this.props.newItems,
          this.props.moduleOpts,
          this.props
        );
        this.setState({ itemsCols: updatedCols, lastColUpdated: colToUpdate });
      } else {
        this.setState({ itemsCols: this.generateColumns(this.props.children, this.state.waterfallCount) });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  handleRef(refName) {
    this[`set${refName}`] = (domEle) => (this[refName] = domEle);
    return this[`set${refName}`];
  }

  onResize(e) {
    const masonryColumnWidth = this.masonryComponent.masonryContainer.clientWidth;
    const layoutWidth = this.container.clientWidth;
    const count = this.calculateColumns(layoutWidth, masonryColumnWidth);

    // We need to regenerate the items columns when the number of columns changes
    if (count !== this.state.waterfallCount) {
      this.setState({
        waterfallCount: count,
        itemsCols:      this.generateColumns(this.props.children, count),
      });
    }
  }

  shouldPrepend(newerItems) {
    return newerItems !== this.props.newItems && !_.isEmpty(newerItems);
  }

  getPinnedValue(item) {
    return _.get(item, 'props.item.meta.pinned');
  }

  isItemPinned(item) {
    return !!this.getPinnedValue(item);
  }

  injectItem(item, colToUpdate) {
    const itemsCols = this.state.itemsCols.slice();
    let colIdx = colToUpdate;
    let rowIdx = _.findIndex(itemsCols[colIdx], (currentItem) => !this.isItemPinned(currentItem));

    // if item has pinned content, find correct col, and idx to achieve pinned position
    if (this.isItemPinned(item)) {
      const idxOfPinValue = this.getPinnedValue(item) - 1;
      rowIdx = Math.floor(idxOfPinValue / this.state.waterfallCount);
      colIdx = idxOfPinValue % this.state.waterfallCount;
    }

    const lastPinInColIdx = _.findLastIndex(itemsCols[colIdx], (currentItem) => this.isItemPinned(currentItem));
    // check if there is a non-pinned place for new item in col, and it would not dislocate an existing pinned item
    const notValidPlacementForItem = rowIdx > -1 && lastPinInColIdx > -1 && rowIdx <= lastPinInColIdx;
    if (notValidPlacementForItem) {
      return undefined;
    } else {
      // inject
      rowIdx = rowIdx > -1 ? rowIdx : itemsCols[colIdx] ? itemsCols[colIdx].length : 0;
      !!itemsCols[colIdx] ? itemsCols[colIdx].splice(rowIdx, 0, item) : (itemsCols[colIdx] = [item]);
      return itemsCols;
    }
  }

  tryPrependItems(newItems, moduleOpts, nextProps) {
    let itemsCols = this.state.itemsCols.slice();
    let lastColUpdated = this.state.lastColUpdated;
    const self = this;
    newItems.forEach((item, itemIdx) => {
      const colToUpdate = (lastColUpdated + 1) % self.state.waterfallCount;
      if (moduleOpts.shouldShowStickyContent) {
        itemsCols = self.injectItem(item, colToUpdate);
        if (!itemsCols) {
          // no valid way to just inject item
          itemsCols = self.generateColumns(nextProps.children, self.state.waterfallCount);
          return false; // break out of iterator
        }
      } else {
        itemsCols[colToUpdate] = !!itemsCols[colToUpdate] ? _.concat(item, itemsCols[colToUpdate]) : [item];
      }
      lastColUpdated = colToUpdate;
    });
    return { updatedCols: itemsCols, colToUpdate: lastColUpdated };
  }

  calculateColumns(containerWidth, columnWidth) {
    // If either of these is zero, null or NaN the component will throw an error
    if (!containerWidth || !columnWidth) {
      return 1;
    } else {
      return Math.round(containerWidth / columnWidth);
    }
  }

  garbageCollectColumns(itemsToRemove, itemsCols) {
    return _.map(itemsCols, (col) => {
      return _.filter(col, (item) => {
        return !_.find(itemsToRemove, (itemToRemove) => {
          return _.get(itemToRemove, 'props.id') === _.get(item, 'props.id');
        });
      });
    });
  }

  generateColumns(children, colNum) {
    const allItems = React.Children.toArray(children);
    return _.reduce(
      allItems,
      (acc, item, idx) => {
        const accIdx = idx % colNum;
        acc[accIdx] = acc[accIdx] ? acc[accIdx].concat([item]) : [item];
        return acc;
      },
      []
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.Modules.SocialContentWall.Waterfall}
   */

  render() {
    const classNames = this.props.className || 'xContainer';
    const masonryColWidthSelector = getMasonryColWidthSelector(this.props.children);
    const waterfallCols = [];
    let i = 0;
    do {
      waterfallCols.push(
        <MasonryComponent
          className="xTemplateWaterFall__column"
          options={{
            originTop:          true,
            transitionDuration: 500,
            stagger:            50,
            columnWidth:        masonryColWidthSelector,
          }}
          ref={i === 0 ? this.handleRef('masonryComponent') : undefined}
          key={`masonryKey${i}`}
          onLayoutComplete={NGX.App.api.resizeParent}
        >
          {this.state.itemsCols[i]}
        </MasonryComponent>
      );
      i++;
    } while (i < this.state.waterfallCount);

    return (
      <div className={classNames} data-ngx-contentsource="external">
        <div className="xContainerInner" ref={this.handleRef('container')}>
          <Choose>
            <When condition={this.props.showMoreMode === enums.SHOW_MORE_MODE_AUTO}>
              <InfiniteScroll
                onLoadMore={this.props.onLoadMore}
                hasMore={this.props.currentPage < this.props.totalPages}
              >
                {waterfallCols}
              </InfiniteScroll>
            </When>
            <Otherwise>{waterfallCols}</Otherwise>
          </Choose>
        </div>
      </div>
    );
  }
}

/**
 * PropTypes.
 *
 * @type {Object}
 * @property {String} className List of css classnames to apply to the containing element.
 */
Waterfall.propTypes = {
  className: PropTypes.string,
};

export { Waterfall };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Display Item > Collection > Stream React component.
 *
 * Summary of responsibilities:
 *
 *  - outputs wrapper elements for Stream collections.
 * </pre>
 *
 * @memberOf Common.Components.DisplayItems.Collections
 * @namespace
 *
 * @return {Common.Components.Modules.Collections.Stream}
 */
class Stream extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
    this.handleRef = this.handleRef.bind(this);
  }

  handleRef(refName) {
    this[`set${refName}`] = (domEle) => (this[refName] = domEle);
    return this[`set${refName}`];
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.Modules.SocialContentWall.Stream}
   */

  render() {
    const classNames = this.props.className || 'xContainer';

    return (
      <div className={classNames} data-ngx-contentsource="external">
        <div className="xContainerInner" ref={this.handleRef('container')}>
          <Choose>
            <When condition={this.props.showMoreMode === enums.SHOW_MORE_MODE_AUTO}>
              <InfiniteScroll
                onLoadMore={this.props.onLoadMore}
                hasMore={this.props.currentPage < this.props.totalPages}
              >
                <MasonryComponent
                  options={{ originTop: true, transitionDuration: '0.5s' }}
                  className="xTemplateStream__column"
                  onLayoutComplete={NGX.App.api.resizeParent}
                >
                  {this.props.children}
                </MasonryComponent>
              </InfiniteScroll>
            </When>
            <Otherwise>
              <MasonryComponent
                options={{ originTop: true, transitionDuration: '0.5s' }}
                className="xTemplateStream__column"
                onLayoutComplete={NGX.App.api.resizeParent}
              >
                {this.props.children}
              </MasonryComponent>
            </Otherwise>
          </Choose>
        </div>
      </div>
    );
  }
}

/**
 * PropTypes.
 *
 * @type {Object}
 * @property {String} className List of css classnames to apply to the containing element.
 */
Stream.propTypes = {
  className: PropTypes.string,
};

export { Stream };
